import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject} from '@angular/core';

import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {Store} from '@ngrx/store';
import {ScreeningCriteriaCreateDTO, ScreeningCriteriaDTO, ScreeningCriteriaEvalDTO} from '../../dto/screening-criteria';
import {
  createScreeningCriteria,
  deleteScreeningCriteria,
  updateScreeningCriteria
} from '../../services/screening-criteria/screening-criteria.actions';
import {getStatement} from '../../helper/screening-criteria';
import {MatRadioModule} from '@angular/material/radio';
import {ScreeningCriteriaService} from '../../services/screening-criteria/screening-criteria.service';
import {MatCardModule} from '@angular/material/card';
import {MatDividerModule} from '@angular/material/divider';
import {CloseableDialogComponent} from "../../../../utils/components/closeable-dialog/closeable-dialog.component";
import {LoadingCardComponent} from '../../../../utils/components/loading-card/loading-card.component';

interface ScreeningCriteriaDialogData {
  criteria?: ScreeningCriteriaDTO;
  projectId: number;
}

@Component({
  selector: 'app-screening-criterias-dialog',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatRadioModule,
    MatButtonModule,
    MatDialogModule,
    MatCardModule,
    MatDividerModule,
    CloseableDialogComponent,
    LoadingCardComponent
],
  templateUrl: './screening-criterias-dialog.component.html',
  styleUrl: './screening-criterias-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningCriteriasDialogComponent {
  private readonly store: Store = inject(Store);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly service: ScreeningCriteriaService = inject(ScreeningCriteriaService);
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly dialogRef: MatDialogRef<ScreeningCriteriasDialogComponent> = inject(MatDialogRef);
  readonly data: ScreeningCriteriaDialogData = inject<ScreeningCriteriaDialogData>(MAT_DIALOG_DATA);

  form: FormGroup = this.fb.group({
    includeCriteria: [this.data?.criteria?.includeCriteria ?? true, Validators.required],
    name: [this.data?.criteria?.name || '', Validators.required],
    criteria: [this.data?.criteria?.criteria || '', Validators.required],
  });

  loadingEvaluation = false;
  evaluation?: ScreeningCriteriaEvalDTO;

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (!this.data.projectId) {
      return;
    }
    if (this.data.criteria && this.data.criteria.id) {
      this.update();
    } else {
      this.create();
    }
    this.dialogRef.close(true);
  }


  delete(): void {
    if (this.data.criteria && this.data.criteria.id) {
      this.store.dispatch(
        deleteScreeningCriteria({
          projectId: this.data.projectId,
          id: this.data.criteria.id,
        })
      );
    }
    this.dialogRef.close(true);
  }

  private create(): void {
    const toCreate: ScreeningCriteriaCreateDTO = this.form.value;
    toCreate.projectId = this.data.projectId;
    this.store.dispatch(
      createScreeningCriteria({
        projectId: this.data.projectId,
        criteria: toCreate,
      })
    );
  }

  private update(): void {
    const toUpdate: ScreeningCriteriaDTO = {...this.data.criteria, ...this.form.value};
    this.store.dispatch(
      updateScreeningCriteria({
        id: toUpdate.id,
        projectId: this.data.projectId,
        criteria: toUpdate
      })
    );
  }

  public evalScreeningCriteria() {
    this.loadingEvaluation = true;
    const toUpdate: ScreeningCriteriaDTO = {...this.data.criteria, ...this.form.value};
    this.service.evalScreeningCriteria(this.data.projectId, toUpdate.criteria).subscribe(
      result => {
        this.evaluation = result;
        this.loadingEvaluation = false;
        this.cdr.detectChanges();
      }
    )
    this.cdr.detectChanges();
  }

  get getPreviewStatement() {
    return getStatement(this.form.get('includeCriteria')?.value ?? true, this.form.get('criteria')?.value);
  }
}
